/**
 * Module Learning Config Editor
 *
 * Module 레벨 학습 활동(시작/종료) 설정 에디터
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, CheckCircle, FileText, Settings, Lock } from 'lucide-react'
import type { ModuleLearningConfig, ModuleActivityConfig, ScopedQuizOptions } from '@/types/configuration'

interface ModuleLearningConfigEditorProps {
  config: ModuleLearningConfig | undefined
  onUpdate: (config: ModuleLearningConfig) => void
  readOnly?: boolean
}

const defaultQuizOptions: ScopedQuizOptions = {
  questionCount: 10,
  shuffleQuestions: true,
  passingScore: 70,
  timeLimitMinutes: undefined,
}

const defaultActivityConfig: ModuleActivityConfig = {
  enabled: false,
  quizOptions: { ...defaultQuizOptions },
}

export const ModuleLearningConfigEditor: React.FC<ModuleLearningConfigEditorProps> = ({
  config,
  onUpdate,
  readOnly,
}) => {
  const { t } = useTranslation('configuration')
  const [expandedSection, setExpandedSection] = useState<'start' | 'end' | null>('start')

  const startActivity = config?.startActivity || { ...defaultActivityConfig }
  const endActivity = config?.endActivity || { ...defaultActivityConfig }

  const handleStartActivityUpdate = (activity: ModuleActivityConfig) => {
    onUpdate({
      ...config,
      startActivity: activity,
      endActivity: config?.endActivity,
    })
  }

  const handleEndActivityUpdate = (activity: ModuleActivityConfig) => {
    onUpdate({
      ...config,
      startActivity: config?.startActivity,
      endActivity: activity,
    })
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-sm text-amber-800" dangerouslySetInnerHTML={{ __html: t('moduleLearning.description') }} />
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 p-3 bg-gray-100 border border-gray-200 rounded-lg">
          <Lock className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">{t('moduleLearning.readOnlyMessage')}</span>
        </div>
      )}

      {!readOnly && (<>
      {/* 시작 활동 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'start' ? null : 'start')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">{t('moduleLearning.startQuiz')}</span>
            {startActivity.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                {t('moduleLearning.enabled')}
              </span>
            )}
          </div>
          <Settings className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'start' ? 'rotate-90' : ''}`} />
        </button>

        {expandedSection === 'start' && (
          <div className="p-4 space-y-4">
            <ModuleActivityConfigSection
              activity={startActivity}
              onUpdate={handleStartActivityUpdate}
              label={t('moduleLearning.startQuiz')}
            />
          </div>
        )}
      </div>

      {/* 종료 활동 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'end' ? null : 'end')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">{t('moduleLearning.endQuiz')}</span>
            {endActivity.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                {t('moduleLearning.enabled')}
              </span>
            )}
          </div>
          <Settings className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'end' ? 'rotate-90' : ''}`} />
        </button>

        {expandedSection === 'end' && (
          <div className="p-4 space-y-4">
            <ModuleActivityConfigSection
              activity={endActivity}
              onUpdate={handleEndActivityUpdate}
              label={t('moduleLearning.endQuiz')}
            />
          </div>
        )}
      </div>
      </>)}
    </div>
  )
}

// 활동 설정 섹션 컴포넌트
interface ModuleActivityConfigSectionProps {
  activity: ModuleActivityConfig
  onUpdate: (activity: ModuleActivityConfig) => void
  label: string
}

const ModuleActivityConfigSection: React.FC<ModuleActivityConfigSectionProps> = ({
  activity,
  onUpdate,
  label,
}) => {
  const { t } = useTranslation('configuration')
  const handleToggle = () => {
    onUpdate({
      ...activity,
      enabled: !activity.enabled,
    })
  }

  const handleQuizOptionsChange = (options: Partial<ScopedQuizOptions>) => {
    onUpdate({
      ...activity,
      quizOptions: {
        ...(activity.quizOptions || defaultQuizOptions),
        ...options,
      },
    })
  }

  return (
    <div className="space-y-4">
      {/* 활성화 토글 */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={activity.enabled}
          onChange={handleToggle}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">{t('moduleLearning.useQuiz', { label })}</span>
      </label>

      {activity.enabled && (
        <div className="p-4 space-y-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-medium text-gray-900">{t('moduleLearning.checkupQuizOptions')}</h4>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('moduleLearning.questionCount')}</label>
              <input
                type="number"
                min={1}
                max={30}
                value={activity.quizOptions?.questionCount ?? 10}
                onChange={(e) => handleQuizOptionsChange({ questionCount: parseInt(e.target.value) || 10 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('moduleLearning.passingScore')}</label>
              <input
                type="number"
                min={0}
                max={100}
                value={activity.quizOptions?.passingScore ?? 70}
                onChange={(e) => handleQuizOptionsChange({ passingScore: parseInt(e.target.value) || 70 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('moduleLearning.timeLimit')}</label>
              <input
                type="number"
                min={0}
                max={60}
                value={activity.quizOptions?.timeLimitMinutes ?? 0}
                onChange={(e) => handleQuizOptionsChange({ timeLimitMinutes: parseInt(e.target.value) || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={activity.quizOptions?.shuffleQuestions ?? true}
              onChange={(e) => handleQuizOptionsChange({ shuffleQuestions: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('moduleLearning.shuffleQuestions')}</span>
          </label>

          <p className="text-xs text-gray-500">
            {t('moduleLearning.quizSourceNote')}
          </p>
        </div>
      )}
    </div>
  )
}

export default ModuleLearningConfigEditor
